import React, { useEffect, useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';
import round from 'lodash/round';
import get from 'lodash/get';
import { GoogleMapsProvider } from '@vmos2/location';
import { APP_CONFIG } from '@vmos2/core';
import { Icon } from '@vmos2/design-system';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';

type Props = {
  position: {
    latitude: number;
    longitude: number;
  };
  height?: string;
  onChange: Function;
  isMapLocked?: boolean;
  setErrors?: Function;
  handleAvailableForDelivery?: Function;
};

export const LocationPin = (props: Props) => {
  const googleApiKey = get(APP_CONFIG, 'GOOGLE_MAPS_API_KEY');

  return (
    <GoogleMapsProvider apiKey={googleApiKey}>
      <LocationPinSection {...props} />
    </GoogleMapsProvider>
  );
};

export const LocationPinSection = ({
  position,
  onChange,
  height = '100%',
  isMapLocked = false,
  setErrors,
  handleAvailableForDelivery,
}: Props) => {
  const theme = useTheme();
  const mapID = get(APP_CONFIG, 'GOOGLE_MAPS_MAP_ID');

  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);

  const [mapPosition, setPosition] = useState({
    lat: position.latitude,
    lng: position.longitude,
  });

  const pinComponent = <Icon name="MapMarker2" size={64} fill={theme.color.brand} />;

  const initMap = async () => {
    if (!mapContainerRef.current) return;

    const { Map } = await google.maps.importLibrary('maps');
    const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');
    const { Geocoder } = await google.maps.importLibrary('geocoding');

    const map = new Map(mapContainerRef.current, {
      center: mapPosition,
      zoom: 18,
      disableDefaultUI: true,
      zoomControl: !isMapLocked,
      mapId: mapID,
      gestureHandling: isMapLocked ? 'none' : 'cooperative',
    });

    mapInstanceRef.current = map;

    const marker = new AdvancedMarkerElement({
      position: mapPosition,
      map,
      gmpDraggable: false,
      content: document
        .createRange()
        .createContextualFragment(renderToString(pinComponent)),
    });

    const geocoder = new Geocoder();

    map.addListener('center_changed', () => {
      const newCenter = mapInstanceRef.current?.getCenter();
      if (!newCenter) return;
      marker.position = {
        lat: round(newCenter.lat(), 6),
        lng: round(newCenter.lng(), 6),
      };
    });

    map.addListener('dragend', async () => {
      const newCenter = mapInstanceRef.current?.getCenter();
      if (!newCenter) return;
      const latitude = round(newCenter.lat(), 6);
      const longitude = round(newCenter.lng(), 6);
      let postalCode: string | null = null;
      try {
        const { results } = await geocoder.geocode({ location: { lat: latitude, lng: longitude } });
        if (results?.[0]?.address_components) {
          for (const component of results[0].address_components) {
            if (component.types.includes('postal_code')) {
              postalCode = component.long_name;
              break;
            }
          }
        }
      } catch (err) {
        console.error('Geocoder failed due to:', err);
      }

      onChange('location', {
        latitude,
        longitude,
      });

      if (handleAvailableForDelivery) {
        const isAvailableForDelivery = handleAvailableForDelivery({
          latitude,
          longitude,
        }, postalCode);

        setErrors && setErrors(!isAvailableForDelivery);
      }

      setPosition({ lat: latitude, lng: longitude });
    });
  };

  useEffect(() => {
    initMap();
  }, [isMapLocked, theme]);

  useEffect(() => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setCenter(mapPosition);
    }
  }, [mapPosition]);

  return (
    <GoogleMap
      ref={mapContainerRef}
      id="googleMap"
      height={height}
      isFullWidth={!isMapLocked}
    />
  );
};

export const GoogleMap = styled('div')`
  height: ${({ height }) => height};
  ${({ isFullWidth }) => isFullWidth && `margin-left: -32px; margin-right: -32px;`}
`;
